import { FC } from 'react';
import { ETablePaginationWithApi } from '@/components/everridge/table/components/ETablePaginationWithApi';
import OwnersListMobileEmpty from '@/features/owners/components/OwnersList/OwnersListMobileEmpty';
import { OwnerListResponseDto } from '@/features/owners/types/owners-dto';
import OwnersListSkeleton from '@/features/owners/components/OwnersList/OwnersListElementkeleton';
import ApartmentIcon from '@/components/icons/ApartmentIcon';
import AgreementIcon from '@/components/icons/AgreementIcon';
import UserIcon from '@/components/icons/UserIcon';
import { Button } from '@/components/ui/button';
import PackageAddIcon from '@/components/icons/PackageAddIcon';
import EAvatar from '@/components/everridge/EAvatar';
import { cn } from '@/lib/utils';
import { OwnersUtils } from '../../utils/ownersUtils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';

type Props = {
	data?: OwnerListResponseDto;
	isLoading?: boolean;
	page: number;
	setPage: (page: number) => void;
	paginationStep?: number;
	showDetails?: (id: string) => void;
	createOrder?: () => void;
};

const OwnersListMobile: FC<Props> = ({
	page,
	setPage,
	paginationStep = 10,
	data,
	isLoading,
	showDetails,
	createOrder,
}) => {
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

	return (
		<div className='max-w-[calc(100vw-32px)] p-0 flex-1 flex flex-col gap-4 rounded-2.5xl bg-popover-30 select-none hover:bg-popover-50'>
			<div className='flex-1'>
				{isLoading && <OwnersListSkeleton isMobile={isMobile} />}
				{!isLoading && !!data?.items?.length && (
					<div className='flex flex-col'>
						{data.items?.map((owner) => {
							const { statusStyles } = OwnersUtils.getStatusData(
								owner?.status,
							);

							return (
								<div
									key={owner.id}
									className='relative -mx-4 p-4 flex items-center gap-4'>
									<div
										className='absolute inset-0'
										onClick={() => showDetails?.(owner.id)}
									/>
									<div className='relative'>
										<EAvatar
											className={'size-11'}
											url={owner.avatarUrl}
										/>
										<div
											className={cn(
												'absolute bottom-0 right-1 size-4 rounded-full border-3 border-popover-10',
												statusStyles,
											)}
										/>
									</div>
									<div className='truncate'>
										<h4 className='text-base font-medium truncate'>
											{owner.name}
										</h4>
										<div className={'flex gap-2'}>
											<div
												className={
													'flex items-center gap-1.5 text-sm text-popover-800'
												}>
												<ApartmentIcon
													className={
														'stroke-popover-800'
													}
												/>
												{owner.apartments}
											</div>
											<div
												className={
													'flex items-center gap-1.5 text-sm text-popover-800'
												}>
												<AgreementIcon
													className={
														'stroke-popover-800'
													}
												/>
												{owner.agreements}
											</div>
											<div
												className={
													'flex items-center gap-1.5 text-sm text-popover-800'
												}>
												<UserIcon
													className={
														'stroke-popover-800'
													}
												/>
												{owner.trustees}
											</div>
										</div>
									</div>
									<Button
										variant='outline'
										size={'icon'}
										className='relative ml-auto'
										onClick={createOrder}>
										<PackageAddIcon />
									</Button>
								</div>
							);
						})}
					</div>
				)}
				{!isLoading && !data?.items?.length && (
					<OwnersListMobileEmpty />
				)}
			</div>
			{!!data?.totalCount && data?.totalCount > 10 && (
				<ETablePaginationWithApi
					totalCount={Math.ceil(data?.totalCount / paginationStep)}
					page={page}
					setPage={setPage}
					range={1}
				/>
			)}
		</div>
	);
};

export default OwnersListMobile;
